<template>
  <ui-component-modal
    :modalTitle="$t('Components.Invoices.ModalSendInvoice.Main_Title')"
    modalSize="large"
    :onClickSave="sendMail"
    :onClickCancel="deleteDraftAndCancel"
    :showModal="showModal"
    :isSaving="isSending"
    :isSavingSuccess="isSendingSuccess"
    :savingSuccessMessage="
      $t('Components.Invoices.ModalSendInvoice.Message_SavingSuccess')
    "
    :isSavingError="isSendingError"
    :savingErrorMessage="
      $t('Components.Invoices.ModalSendInvoice.Message_SavingError', {
        message: errorMessage,
      })
    "
    :buttonLabelConfirm="$t('Components.Invoices.ModalSendInvoice.Button_Send')"
    :disableSaveButton="
      selectedMailId === '' || isSending || !validEmailSettings
    "
    :hideFooterButtons="isSendingError || isSendingSuccess"
    @closeModal="deleteDraftAndCancel"
  >
    <template v-slot:content>
      <div v-if="invoice">
        <div v-if="!isSendingError && !isSendingSuccess">
          <Message
            :message="
              $t('Components.Invoices.ModalSendInvoice.Message_ConceptStatus')
            "
            :type="'is-danger'"
            v-if="invoice.StatusId === 0"
          />

          <table v-if="selectedMail" class="table is-fullwidth">
            <tbody>
              <tr>
                <td>
                  {{ $t('Components.Invoices.ModalSendInvoice.Label_From') }}
                </td>
                <td>
                  <ui-base-input-field
                    fieldType="text"
                    :showLabel="false"
                    v-model="fromEmail"
                    :error="fromEmailErrors"
                    placeholder="Add mailaddress"
                    @input="$v.fromEmail.$touch()"
                    @blur="$v.fromEmail.$touch()"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  {{ $t('Components.Invoices.ModalSendInvoice.Label_To') }}
                </td>
                <td>
                  <MailInput
                    :mailaddresses="mailAddresses"
                    @mailinputChecked="setMailInputEmpty"
                    @mailaddressAdded="addMailAddress"
                    @mailaddressDeleted="deleteMailAddress"
                    @emailErrorPresent="hasToAddressWarning"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <a @click="showBccInput = !showBccInput">{{
                    $t('Components.Invoices.ModalSendInvoice.Label_Bcc')
                  }}</a>
                </td>
                <td>
                  <MailInput
                    v-if="showBccInput"
                    :mailaddresses="bccAddresses"
                    @mailinputChecked="setMailInputEmpty"
                    @mailaddressAdded="addBCCAddress"
                    @mailaddressDeleted="deleteBCCAddress"
                    @emailErrorPresent="hasToAddressWarning"
                  />
                </td>
              </tr>

              <tr v-if="!mailTypeAction">
                <td width="100">
                  {{
                    $t('Components.Invoices.ModalSendInvoice.Label_MailType')
                  }}
                </td>
                <td>
                  <div class="select" :class="{ 'is-loading': isLoadingMails }">
                    <select @change="setSelectedMail">
                      <option
                        v-for="(mail, index) in mails"
                        :key="index"
                        :value="mail.Id"
                        :selected="mail.Id === selectedMailId"
                        v-text="
                          $t(
                            `Components.Invoices.ModalSendInvoice.Select_Option${mail.MailType.replace(
                              'LocationInvoice',
                              ''
                            )}`
                          )
                        "
                      ></option>
                    </select>
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  {{ $t('Components.Invoices.ModalSendInvoice.Label_Subject') }}
                </td>
                <td>
                  <ui-base-input-field
                    :showLabel="false"
                    fieldType="text"
                    placeholder="Subject"
                    :error="subjectErrors"
                    v-model="selectedMail.Subject"
                    @input="$v.selectedMail.Subject.$touch()"
                    @blur="$v.selectedMail.Subject.$touch()"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  {{ $t('Components.Invoices.ModalSendInvoice.Label_Mail') }}
                  <br />
                  <transition name="fade" mode="out-in">
                    <span v-if="showSpinner" class="tag is-link">
                      <span
                        class="icon"
                        :class="{ 'is-loading': showSpinner }"
                      ></span>
                      <span>saving </span>
                    </span>
                  </transition>
                </td>
                <td>
                  <div class="control">
                    <ui-text-editor
                      :key="'editor' + selectedMail.Id"
                      v-model="selectedMail.Body"
                      @input="
                        () => {
                          delay(saveDraft(selectedMail), 700)
                          $v.selectedMail.Body.$touch()
                        }
                      "
                      @blur="$v.selectedMail.Body.$touch()"
                      :class="{ cke_chrome: hasBodyWarning }"
                    ></ui-text-editor>
                  </div>
                  <div v-if="bodyErrors.length" class="has-text-danger">
                    <small>{{ bodyErrors }}</small>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <section v-if="isSendingSuccess" class="section">
          <div class="container has-text-centered">
            <SuccessAnimation />

            <div class="title">
              {{
                $t('Components.Invoices.ModalSendInvoice.Message_InvoiceSent')
              }}
            </div>
          </div>
        </section>

        <section v-if="isSendingError" class="section">
          <div class="container has-text-centered">
            <ErrorAnimation />

            <div class="title">
              {{ $t('Components.Invoices.ModalSendInvoice.Message_Oops') }}
            </div>
          </div>
        </section>
      </div>
    </template>
  </ui-component-modal>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { EventBus } from '@/eventbus/event-bus'
import invoiceProvider from '@/providers/invoice'
import mailProvider from '@/providers/mail'
import { required, email, minLength } from 'vuelidate/lib/validators'

const MailInput = () => import('@/components/UI/Components/MailInput')
const Message = () => import('@/components/UI/Message')
const ErrorAnimation = () => import('@/components/UI/Components/ErrorAnimation')
const SuccessAnimation = () =>
  import('@/components/UI/Components/SuccessAnimation')

export default {
  components: {
    MailInput,
    ErrorAnimation,
    SuccessAnimation,
    Message,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    onClickCancel: {
      type: Function,
      required: true,
    },

    invoiceId: {
      type: Number,
      default: 0,
    },

    mailTypeAction: {
      type: String,
      default: '',
    },
    callbackAction: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      errorMessage: '',
      fromEmail: '',
      invoiceSettings: null,
      isLoadingMails: false,
      isSendingError: false,
      isSendingSuccess: false,
      isSending: false,
      bccAddresses: [],
      mailAddresses: [],
      mails: [],
      selectedMail: null,
      sendCounter: 0,
      showBccInput: false,
      addressWarning: false,
      draftPresent: false,
      showSpinner: false,
      timer: null,
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      fromEmail: {
        required,
        email,
      },
      selectedMail: {
        Subject: {
          required,
          minLength: minLength(3),
        },
        Body: {
          required,
          minLength: minLength(10),
        },
      },
    }
    return output
  },

  computed: {
    ...mapState('invoiceStore', ['invoice']),
    ...mapState('locationStore', ['locationState']),
    ...mapState('profileStore', ['profile']),

    fromEmailErrors() {
      const errors = []
      if (!this.$v.fromEmail.$dirty) return errors
      if (!this.$v.fromEmail.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      } else if (!this.$v.fromEmail.email) {
        errors.push(this.$t('Form.InputErrors.InvalidEmail'))
      }
      return errors
    },

    subjectErrors() {
      const errors = []
      if (!this.$v.selectedMail.Subject.$dirty) return errors
      !this.$v.selectedMail.Subject.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.selectedMail.Subject.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 3 }))
      return errors
    },

    bodyErrors() {
      let errors = ''
      if (!this.$v.selectedMail.Body.$dirty) return errors
      if (!this.$v.selectedMail.Body.required) {
        errors = this.$t('Form.InputErrors.Required')
      }
      if (!this.$v.selectedMail.Body.minLength) {
        errors = this.$t('Form.InputErrors.minLength', { minLength: 3 })
      }
      return errors
    },

    selectedMailId() {
      let id = ''

      if (this.selectedMail && this.selectedMail !== null) {
        id = this.selectedMail.Id
      }

      return id
    },

    validEmailSettings() {
      let result = true

      if (
        this.hasFromAddressWarning ||
        this.addressWarning ||
        this.hasSubjectWarning ||
        this.hasBodyWarning
      ) {
        result = false
      }

      return result
    },

    hasFromAddressWarning() {
      let result = false

      if (this.fromEmailErrors.length > 0) {
        result = true
      }

      return result
    },

    hasSubjectWarning() {
      let result = false

      if (this.subjectErrors.length > 0 || !this.selectedMail.Subject) {
        this.$v.$touch()
        result = true
      }

      return result
    },

    hasBodyWarning() {
      let result = false

      if (this.bodyErrors.length > 0 || !this.selectedMail.Body) {
        this.$v.$touch()
        result = true
      }

      return result
    },
  },

  created() {
    this.fromEmail = this.profile.Email

    if (
      !this.invoice ||
      (this.invoiceId !== 0 && this.invoice.Id !== this.invoiceId)
    ) {
      this.setInvoice(null)
      this.getInvoiceData()
    } else {
      this.getInvoiceMails()
      this.getLocationInvoiceSettings()
    }
  },

  beforeDestroy() {
    this.draftPresent = false
  },

  methods: {
    ...mapMutations('invoiceStore', ['setInvoice']),

    hasToAddressWarning(emailError) {
      if (emailError.length > 1) {
        this.addressWarning = true
      } else {
        this.addressWarning = false
      }
    },

    addMailAddress(address) {
      this.mailAddresses.unshift(address)
    },

    deleteMailAddress(address) {
      let addressIndex = this.mailAddresses.findIndex((t) => t === address)
      if (addressIndex > -1) {
        this.mailAddresses.splice(addressIndex, 1)
      }
    },

    addBCCAddress(address) {
      this.bccAddresses.unshift(address)
    },

    deleteBCCAddress(address) {
      let addressIndex = this.bccAddresses.findIndex((t) => t === address)
      if (addressIndex > -1) {
        this.bccAddresses.splice(addressIndex, 1)
      }
    },

    setMailInputEmpty(value) {
      if (value) {
        this.addressWarning = false
      }
    },

    getInvoiceData() {
      let self = this

      invoiceProvider.methods
        .getInvoiceById(this.invoiceId)
        .then((response) => {
          if (response.status === 200) {
            self.setInvoice(response.data)

            self.getInvoiceMails()
            self.getLocationInvoiceSettings()
          }
        })
        .finally(() => {
          self.pageLoadReady = true
        })
    },

    async getInvoiceMails() {
      let self = this

      if (!self.isLoadingMails) {
        self.isLoadingMails = true

        // Check first if a draft exists
        await this.checkForDrafts()

        self.mailAddresses.push(this.invoice.EmailTo)

        if (self.invoiceSettings && self.invoiceSettings.AutoCC.length > 0) {
          self.showBccInput = true
          self.bccAddresses.push(self.invoiceSettings.AutoCC)
        }

        mailProvider.methods
          .getInvoiceMailTextsByInvoiceId(self.invoice.Id)
          .then((response) => {
            if (response.status === 200) {
              self.mails = response.data
              self.mails.push({
                Bcc: null,
                Body: '',
                Cc: '',
                FromEmail: '',
                MailType: 'Blanco',
                Subject: '',
              })

              if (!this.draftPresent) {
                if (self.mailTypeAction !== '') {
                  self.selectedMail = self.mails.find(
                    (m) => m.MailType === self.mailTypeAction
                  )
                } else {
                  if (self.invoice.StatusId < 3) {
                    if (self.invoice.IsDeposit) {
                      self.selectedMail = self.mails.find(
                        (m) => m.MailType === 'LocationInvoiceDeposit'
                      )
                    } else {
                      self.selectedMail = self.mails.find(
                        (m) => m.MailType === 'LocationInvoiceStandard'
                      )
                    }
                  }

                  if (self.invoice.StatusId === 3) {
                    self.selectedMail = self.mails.find(
                      (m) => m.MailType === 'LocationInvoiceCredit'
                    )
                  }
                }
              }

              if (self.selectedMail) {
                // self.bccAddresses =
                //   self.selectedMail.Cc.length > 0
                //     ? self.selectedMail.Cc.trim().split(',')
                //     : []
                // self.mailAddresses =
                //   self.selectedMail.ToEmail.length > 0
                //     ? self.selectedMail.ToEmail.trim().split(',')
                //     : ''
              } else {
                self.selectedMail = {
                  Bcc: null,
                  Body: '',
                  Cc: '',
                  FromEmail: '',
                  MailType: 'Blanco',
                  Subject: '',
                }
              }
            }
          })
          .finally(() => {
            self.isLoadingMails = false
          })
      }
    },

    getLocationInvoiceSettings() {
      let self = this
      invoiceProvider.methods
        .getLocationInvoiceSettings(this.locationState.Id)
        .then((response) => {
          if (response.status === 200) {
            this.invoiceSettings = response.data

            if (this.invoiceSettings.DefaultSenderAddress.length > 0) {
              this.fromEmail = '';
              this.fromEmail = self.invoiceSettings.DefaultSenderAddress;
              this.bccAddresses = [];
              this.showBccInput = self.invoiceSettings.AutoCC.length > 0
              this.bccAddresses.push(self.invoiceSettings.AutoCC);
            }
          }
        })
        .finally(() => {})
    },

    async checkForDrafts() {
      await this.$localforge.get('selectedInvoiceDraft').then((response) => {
        if (response) {
          this.selectedMail = response
          this.draftPresent = true
        }
      })
    },

    delay(callback, ms) {
      let self = this
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        callback
        this.savingSpinner()
        clearTimeout(self.timer)
      }, ms || 0)
    },

    setSelectedMail(e) {
      const SELECTVALUE = Number(e.target.value)
      this.selectedMail = { ...this.mails.find((m) => m.Id === SELECTVALUE) }
    },

    deleteDraftAndCancel() {
      this.$localforge.delete('selectedInvoiceDraft')
      this.onClickCancel()
    },

    saveDraft(mail) {
      this.$localforge.set('selectedInvoiceDraft', mail)
    },

    savingSpinner() {
      let self = this
      if (this.secondTimer) {
        clearTimeout(this.secondTimer)
      }
      this.showSpinner = true
      this.secondTimer = setTimeout(() => {
        this.showSpinner = false
        clearTimeout(self.secondTimer)
      }, 600)
    },

    sendMail() {
      let self = this

      self.sendCounter++

      if (!self.isSending) {
        self.isSending = true
        this.$v.$touch()
        if (this.$v.$invalid) {
          // 'ERROR'
          self.isSending = false
        } else {
          let mail = {
            FromEmail: self.fromEmail,
            ToEmail: self.mailAddresses.join(','),
            CC: self.bccAddresses.join(','),
            Subject: self.selectedMail.Subject,
            Body: self.selectedMail.Body,
          }

          mailProvider.methods
            .sendInvoiceMail(self.invoice.Id, mail)
            .then((response) => {
              if (response.status === 200) {
                if (
                  response.data.StatusCode === 200 ||
                  response.data.StatusCode === 202
                ) {
                  self.isSendingSuccess = true

                  self.$emit('mailSend', response.data.Result)
                  // Send message to display toast on screen
                  EventBus.$emit('invoiceMailSend', response.data.Result);

                  setTimeout(() => {
                    self.deleteDraftAndCancel()
                  }, 1000)
                } else {
                  self.isSendingError = true

                  self.errorMessage = response.data.Message
                }
              }
            })
            .catch((e) => {
              self.isSendingError = true
            })
            .finally(() => {
              self.isSending = false
            })
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cke_chrome {
  border-radius: 4px;
  border: 1px solid red;
  border-width: thin;
}
// fade transition
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.25s;
}
</style>
